import React, { useMemo } from 'react';
import { Button, Grid2, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { EntityService } from 'core/api/services/lookUp';

import { LookUpExperience, LookUpExperiencer, LookUpHost, DateRangePickerWithInput, DateFormattedUtils, SelectCheckbox, TIME_ZONES } from '@utriper/core-ui';

import { BookingOrigin, BookingStatus } from '@utriper/core';
import { useReport } from 'core/contexts/reportsFilter';

const ReportFilter = () => {
    const { filters, updateFilter, loadingApp, loadingHost, getHostReports, getAppReports, view } = useReport();

    const { t: translate, i18n } = useTranslation();

    const statusOptions = useMemo(
        () =>
            BookingStatus.getIds().map((id) => ({
                name: BookingStatus.getTranslatedName(BookingStatus.status, id, i18n.language)?.[0] ?? '',
                id,
            })),
        [i18n.language]
    );

    const bookingOriginOptions = useMemo(
        () =>
            BookingOrigin.getIds().map((id) => ({
                name: BookingOrigin.getTranslatedName(BookingOrigin.types, id, i18n.language)?.[0] ?? '',
                id,
            })),
        [i18n.language]
    );

    const handleDateRangeChange = ({ startDate, endDate }) => {
        if (!startDate) {
            toast.info(translate('reports.filters.start_date_required'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        const minAllowedDate = DateTime.utc(2026, 1, 1, 0, 0);

        // Validation 1: No dates earlier than 01/01/2026
        if (startDate.toMillis() < minAllowedDate.toMillis()) {
            toast.error(translate('reports.filters.date_too_early'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        // Validation 2: The end date is later than the start date.
        if (endDate && endDate.toMillis() < startDate.toMillis()) {
            toast.error(translate('reports.filters.date_too_early'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        // Validation 3: No range bigger than 365 days
        if (endDate) {
            const daysDifference = Math.ceil(endDate.diff(startDate, 'days')?.days);
            if (daysDifference > 365) {
                toast.error(translate('reports.filters.range_too_large'), { position: 'top-right', theme: 'colored' });
                return false;
            }
        }

        updateFilter('startDate', startDate);
        updateFilter('endDate', endDate);
    };

    const isLoading = loadingApp || loadingHost;

    const handleFetchReports = async () => {
        if (!filters?.startDate) {
            toast.info(translate('reports.filters.start_date_required'), { position: 'top-right', theme: 'colored' });
            return;
        }

        if (view === 'host') {
            await getHostReports();
        } else {
            await getAppReports();
        }
    };

    return (
        <Grid2 container spacing={1} width="full" size={12} marginBottom={2} flexWrap={{ xs: 'wrap', lg: 'nowrap' }}>
            <DateRangePickerWithInput
                value={{ startDate: filters?.startDate, endDate: filters?.endDate }}
                onChange={handleDateRangeChange}
                style={{
                    width: '100%',
                    minWidth: '280px',
                }}
                useUTC
            />

            <Select style={{ width: '100%' }} variant="outlined" labelId="timezone" id="timezone" value={filters.timezone} onChange={(e) => updateFilter('timezone', e?.target?.value)}>
                {TIME_ZONES.map((tz) => (
                    <MenuItem key={tz.id} style={{ color: '#131313' }} value={tz.id}>
                        {tz.name}
                    </MenuItem>
                ))}
            </Select>

            <SelectCheckbox options={statusOptions} values={filters.paymentStatus || []} label={translate('reports.filters.status')} onChange={(e) => updateFilter('paymentStatus', e)} disabled={isLoading} style={{ width: '100%', minWidth: { xs: '280px', lg: 'auto' } }} />

            <SelectCheckbox
                label={translate('reports.filters.origin')}
                options={bookingOriginOptions}
                values={filters.originIds || []}
                onChange={(e) => {
                    const value = e;
                    updateFilter('originIds', value);
                }}
                disabled={isLoading}
                style={{ width: '100%', minWidth: { xs: '280px', lg: 'auto' } }}
            />

            <Grid2 sx={{ width: '100%', minWidth: { xs: '280px', lg: 'auto' } }}>
                <LookUpHost label={translate('reports.filters.host')} value={filters.hostIds} onChange={(val) => updateFilter('hostIds', Array.isArray(val) ? val.map((v) => v) : [val])} entityService={EntityService} disabled={isLoading} multiSelect />
            </Grid2>

            <Grid2 sx={{ width: '100%', minWidth: { xs: '280px', lg: 'auto' } }}>
                <LookUpExperience label={translate('reports.filters.experience')} value={filters.experienceIds} onChange={(val) => updateFilter('experienceIds', Array.isArray(val) ? val.map((v) => v) : [val])} entityService={EntityService} disabled={isLoading} multiSelect />
            </Grid2>

            <Grid2 sx={{ width: '100%', minWidth: { xs: '280px', lg: 'auto' } }}>
                <LookUpExperiencer label={translate('reports.filters.experiencer')} value={filters.experiencerIds} onChange={(val) => updateFilter('experiencerIds', Array.isArray(val) ? val.map((v) => v) : [val])} entityService={EntityService} disabled={isLoading} multiSelect />
            </Grid2>

            {/* <SelectCheckbox
                label="Método de Pago"
                options={paymentMethodsOptions}
                values={filters.paymentMethods || []}
                onChange={(e) => {
                    const selected = paymentMethodsOptions.find((opt) => opt.id === e);
                    updateFilter('paymentMethods', selected);
                }}
                disabled={loading}
                style={{ width: '100%', minWidth: { xs: '280px', lg: 'auto' } }}
            /> */}

            <Button variant="outlined" color="primary" onClick={handleFetchReports} disabled={isLoading} sx={{ textTransform: 'none', fontWeight: 500, minWidth: 'fit-content' }}>
                {translate('reports.filters.get')}
            </Button>
        </Grid2>
    );
};

export default ReportFilter;
